import { type RefObject, useEffect, useRef, useState } from 'react'

type ReverseInfiniteScrollOptions = {
  items: { id: number }[] | undefined
  isLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  resetKey: string
}

/**
 * Chat-style scrolling: the newest item sits at the bottom, older pages load when the top
 * sentinel becomes visible, and the viewport keeps its position while they are prepended.
 */
export const useReverseInfiniteScroll = ({
  items,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  resetKey
}: ReverseInfiniteScrollOptions): {
  containerRef: RefObject<HTMLDivElement>
  topSentinelRef: RefObject<HTMLDivElement>
  bottomRef: RefObject<HTMLDivElement>
} => {
  const containerRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const prevScrollHeightRef = useRef(0)
  const latestItemIdRef = useRef<number | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the conversation changes
  useEffect(() => {
    setScrolledToBottom(false)
  }, [resetKey])

  useEffect(() => {
    if (!isLoading && items && !scrolledToBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'instant' })
      setScrolledToBottom(true)
    }
  }, [isLoading, items, scrolledToBottom])

  useEffect(() => {
    const latestId = items?.[0]?.id ?? null
    if (
      scrolledToBottom &&
      latestId !== null &&
      latestItemIdRef.current !== null &&
      latestId !== latestItemIdRef.current
    ) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    latestItemIdRef.current = latestId
  }, [items, scrolledToBottom])

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when a prepended page lands
  useEffect(() => {
    const container = containerRef.current
    if (!container || isFetchingNextPage || !prevScrollHeightRef.current) return
    const delta = container.scrollHeight - prevScrollHeightRef.current
    if (delta > 0) {
      container.scrollTop += delta
      prevScrollHeightRef.current = 0
    }
  }, [isFetchingNextPage, items])

  useEffect(() => {
    const sentinel = topSentinelRef.current
    const container = containerRef.current
    if (!sentinel || !container || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          prevScrollHeightRef.current = container.scrollHeight
          fetchNextPage()
        }
      },
      { root: container, threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return { containerRef, topSentinelRef, bottomRef }
}
